import { styled } from '../../shared/Theme/theme';
import { oversightActionPriority } from '../../../models/entities/businessPartner/accountingOversight';

export const Page = styled.div`
    width: 100%;
    padding: 4px 16px 32px;
    color: #1f2937;
`;

export const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.sectionTitle};
`;

export const FilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 14px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 10px;
`;

export const FilterField = styled.div`
    min-width: 200px;
    flex: 1;
    max-width: 280px;
`;

export const KpiRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const kpiCardBase = `
    border-radius: 10px;
    padding: 14px 16px;
    min-height: 96px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #fff;
`;

export const KpiCard = styled.button<{ $variant?: 'danger' | 'warn' | 'info' | 'primary' }>`
    ${kpiCardBase}
    border: 1px solid ${(p) => (p.$variant === 'danger' ? '#f8c4c4' : '#ececec')};
    background: ${(p) => (p.$variant === 'danger' ? '#fff5f5' : '#fff')};
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: #c7d2fe;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

export const KpiLabel = styled.div`
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
`;

export const KpiValue = styled.div<{ $tone?: 'danger' | 'warn' | 'success' | 'info' }>`
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    color: ${(p) =>
        p.$tone === 'danger' ? '#c0392b' : p.$tone === 'warn' ? '#a56b1c' : p.$tone === 'success' ? '#15803d' : '#1f7ab8'};
`;

export const KpiSub = styled.div`
    font-size: 12px;
    color: #6b7280;
`;

export const MainGrid = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 14px;
    margin-bottom: 16px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

export const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
`;

export const ChartCard = styled.div`
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 10px;
    padding: 12px 14px 8px;
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
`;

export const ChartTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
`;

export const Section = styled.section`
    margin-bottom: 16px;
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
`;

export const SectionTitle = styled.h2`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.sectionTitle};
`;

export const TableCard = styled.div`
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const Th = styled.th<{ $right?: boolean; $center?: boolean }>`
    text-align: ${(p) => (p.$right ? 'right' : p.$center ? 'center' : 'left')};
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
    background: #f9fafb;
    border-bottom: 1px solid #ececec;
`;

export const Td = styled.td<{ $right?: boolean; $center?: boolean }>`
    padding: 10px 14px;
    font-size: 13px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    text-align: ${(p) => (p.$right ? 'right' : p.$center ? 'center' : 'left')};
    font-variant-numeric: tabular-nums;
`;

export const Tr = styled.tr<{ $muted?: boolean; $totals?: boolean }>`
    background: ${(p) => (p.$totals ? '#f3f4f6' : p.$muted ? '#fafafa' : undefined)};
    &:hover {
        background: ${(p) => (p.$totals ? '#f3f4f6' : '#fafbfc')};
    }
    &:last-child td {
        border-bottom: none;
    }
`;

export const EmptyTd = styled.td`
    padding: 24px 14px;
    text-align: center;
    color: #6b7280;
    font-size: 13px;
`;

export const ShipmentLink = styled.button`
    border: none;
    background: none;
    color: #2563eb;
    cursor: pointer;
    padding: 0;
    font-size: inherit;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
`;

export const TextLink = styled.button`
    border: none;
    background: none;
    color: #2563eb;
    cursor: pointer;
    padding: 0;
    font-size: 12px;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
`;

export const EmptyHint = styled.p`
    color: #6b7280;
    font-size: 14px;
    padding: 24px 8px;
`;

export const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ActionCard = styled.div<{ $priority: oversightActionPriority }>`
    border: 1px solid
        ${(p) => (p.$priority === 'high' ? '#fecaca' : p.$priority === 'medium' ? '#fed7aa' : '#bbf7d0')};
    border-left: 3px solid
        ${(p) => (p.$priority === 'high' ? '#ef4444' : p.$priority === 'medium' ? '#f97316' : '#22c55e')};
    border-radius: 10px;
    padding: 12px 14px;
    background: #fff;
`;

export const ActionTop = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
`;

export const ActionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
`;

export const PriorityBadge = styled.span<{ $priority: oversightActionPriority }>`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 10px;
    color: #fff;
    background: ${(p) => (p.$priority === 'high' ? '#ef4444' : p.$priority === 'medium' ? '#f97316' : '#22c55e')};
    flex-shrink: 0;
`;

export const ActionStep = styled.div`
    font-size: 12px;
    color: #4b5563;
    margin-top: 4px;
`;

export const ActionMeta = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 6px;
`;

export const ActionFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #f3f4f6;
`;

export const ActionImpact = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
`;

export const ActionButtons = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const SecondaryBtn = styled.button`
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #d8dde3;
    background: #fff;
    color: #344054;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background: #f7f9fc;
    }
`;

export const PrimaryBtn = styled(SecondaryBtn)`
    background: ${(p) => p.theme.colors.primaryBlue};
    border-color: ${(p) => p.theme.colors.primaryBlue};
    color: #fff;

    &:hover {
        opacity: 0.92;
        background: ${(p) => p.theme.colors.primaryBlue};
    }
`;

export const RecoverButton = styled(PrimaryBtn)`
    height: 32px;
    padding: 0 14px;
`;

export const DrawerOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.4);
    z-index: 1200;
`;

export const Drawer = styled.aside`
    position: fixed;
    top: 0;
    right: 0;
    width: min(440px, 100vw);
    height: 100vh;
    background: #fff;
    z-index: 1201;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
`;

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #ececec;

    h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
    }

    button {
        border: none;
        background: none;
        font-size: 22px;
        cursor: pointer;
        color: #6b7280;
    }
`;

export const DrawerBody = styled.div`
    padding: 14px 16px;
    overflow-y: auto;
    flex: 1;
`;

export const LeakLinks = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const LeakChip = styled.button`
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 12px;
    color: #334155;
    cursor: pointer;

    &:hover {
        border-color: #c7d2fe;
        background: #eff6ff;
    }

    strong {
        color: #1e293b;
    }
`;

export const NativeCurrencyPanel = styled.div`
    margin-top: 10px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;
    font-size: 12px;
    color: #475569;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
